import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import {
  alertService,
  type AlertHistoryEntry,
  type AlertRule,
} from "@/services/alert.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bell,
  BellOff,
  Check,
  CheckCircle2,
  Clock,
  Eye,
  Filter,
  Pencil,
  Plus,
  RefreshCw,
  ShieldAlert,
  ToggleLeft,
  ToggleRight,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

// ── Severity helpers ───────────────────────────────────────

const SEVERITY_CONFIG = {
  critical: {
    label: "Critical",
    color: "bg-red-100 text-red-700 border-red-200",
    dotColor: "bg-red-500",
    icon: ShieldAlert,
  },
  warning: {
    label: "Warning",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    dotColor: "bg-amber-500",
    icon: AlertTriangle,
  },
  info: {
    label: "Info",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    dotColor: "bg-blue-500",
    icon: Bell,
  },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "bg-red-50 text-red-700" },
  acknowledged: { label: "Acknowledged", color: "bg-amber-50 text-amber-700" },
  resolved: { label: "Resolved", color: "bg-emerald-50 text-emerald-700" },
  snoozed: { label: "Snoozed", color: "bg-slate-50 text-slate-600" },
};

function SeverityBadge({ severity }: { severity: string }) {
  const config =
    SEVERITY_CONFIG[severity as keyof typeof SEVERITY_CONFIG] ||
    SEVERITY_CONFIG.info;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${config.color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.color}`}
    >
      {config.label}
    </span>
  );
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Tabs ───────────────────────────────────────────────────

type Tab = "alerts" | "rules";

export default function AlertDashboardPage() {
  const [activeTab, setActiveTab] = useState<Tab>("alerts");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alert Dashboard"
        subtitle="Monitor alerts and manage alert rules"
      />

      {/* Tab Nav */}
      <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        <TabButton
          active={activeTab === "alerts"}
          onClick={() => setActiveTab("alerts")}
          icon={Bell}
          label="Alert Feed"
        />
        <TabButton
          active={activeTab === "rules"}
          onClick={() => setActiveTab("rules")}
          icon={Zap}
          label="Alert Rules"
        />
      </div>

      {activeTab === "alerts" ? <AlertFeed /> : <AlertRules />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Bell;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ── Alert Feed ─────────────────────────────────────────────

function AlertFeed() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("open");
  const [severityFilter, setSeverityFilter] = useState("all");

  const { data: summary } = useQuery({
    queryKey: ["alert-summary"],
    queryFn: () => alertService.getAlertSummary(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  const { data: historyData, isLoading } = useQuery({
    queryKey: ["alert-history", statusFilter, severityFilter],
    queryFn: () =>
      alertService.getAlertHistory({
        status: statusFilter !== "all" ? statusFilter : undefined,
        severity: severityFilter !== "all" ? severityFilter : undefined,
        limit: 50,
      }),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => alertService.acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-history"] });
      queryClient.invalidateQueries({ queryKey: ["alert-summary"] });
      toast.success("Alert acknowledged");
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertService.resolveAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-history"] });
      queryClient.invalidateQueries({ queryKey: ["alert-summary"] });
      toast.success("Alert resolved");
    },
  });
  const snoozeMutation = useMutation({
    mutationFn: (id: string) => {
      const until = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      return alertService.snoozeAlert(id, until);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-history"] });
      queryClient.invalidateQueries({ queryKey: ["alert-summary"] });
      toast.success("Alert snoozed for one hour");
    },
  });

  const evaluateMutation = useMutation({
    mutationFn: () => alertService.triggerEvaluation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-history"] });
      queryClient.invalidateQueries({ queryKey: ["alert-summary"] });
      toast.success("Alert evaluation triggered");
    },
  });

  const alerts = historyData?.alerts || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryCard
            icon={XCircle}
            label="Open"
            value={summary.openAlerts}
            color="bg-red-50 text-red-600"
          />
          <SummaryCard
            icon={Eye}
            label="Acknowledged"
            value={summary.acknowledgedAlerts}
            color="bg-amber-50 text-amber-600"
          />
          <SummaryCard
            icon={ShieldAlert}
            label="Critical"
            value={summary.criticalAlerts}
            color="bg-rose-50 text-rose-600"
          />
          <SummaryCard
            icon={Clock}
            label="Today"
            value={summary.alertsToday}
            color="bg-blue-50 text-blue-600"
          />
        </div>
      )}

      {/* Filters + Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="snoozed">Snoozed</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>

        <button
          onClick={() => evaluateMutation.mutate()}
          disabled={evaluateMutation.isPending}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 ${
              evaluateMutation.isPending ? "animate-spin" : ""
            }`}
          />
          Run Evaluation
        </button>
      </div>

      {/* Alert List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white py-20 text-slate-400">
            <CheckCircle2 className="mb-3 h-12 w-12" />
            <p className="text-sm font-medium">No alerts found</p>
            <p className="text-xs">
              Everything looks good, or adjust your filters
            </p>
          </div>
        ) : (
          alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onAcknowledge={() => acknowledgeMutation.mutate(alert.id)}
              onResolve={() => resolveMutation.mutate(alert.id)}
              onSnooze={() => snoozeMutation.mutate(alert.id)}
            />
          ))
        )}
      </div>

      {historyData && (
        <p className="text-center text-xs text-slate-400">
          Showing {alerts.length} of {historyData.total} alerts
        </p>
      )}
    </div>
  );
}

function AlertCard({
  alert,
  onAcknowledge,
  onResolve,
  onSnooze,
}: {
  alert: AlertHistoryEntry;
  onAcknowledge: () => void;
  onResolve: () => void;
  onSnooze: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 transition-all hover:shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <SeverityBadge severity={alert.severity} />
            <StatusBadge status={alert.status} />
            <span className="text-xs text-slate-400">
              {timeAgo(alert.firedAt)}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-slate-900 truncate">
            {alert.title}
          </h4>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
            {alert.message}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            {alert.tenant && <span>Tenant: {alert.tenant.name}</span>}
            {alert.rule && <span>Rule: {alert.rule.name}</span>}
          </div>
        </div>

        {/* Actions */}
        {(alert.status === "open" || alert.status === "acknowledged") && (
          <div className="flex items-center gap-1.5 shrink-0">
            {alert.status === "open" && (
              <button
                onClick={onAcknowledge}
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
                title="Acknowledge"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={onResolve}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
              title="Resolve"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onSnooze}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
              title="Snooze for one hour"
            >
              <Clock className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Alert Rules Tab ────────────────────────────────────────

function AlertRules() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: rules, isLoading } = useQuery({
    queryKey: ["alert-rules"],
    queryFn: () => alertService.getAllRules(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      alertService.updateRule(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
      toast.success("Rule updated");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertService.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
      toast.success("Rule deleted");
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white"
        >
          <Plus className="h-4 w-4" />
          New alert rule
        </button>
      </div>
      {showForm && (
        <AlertRuleForm
          rule={editing}
          onClose={() => setShowForm(false)}
          onChanged={() => {
            setShowForm(false);
            queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
          }}
        />
      )}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        </div>
      ) : !rules || rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white py-20 text-slate-400">
          <BellOff className="mb-3 h-12 w-12" />
          <p className="text-sm font-medium">No alert rules configured</p>
          <p className="text-xs">Run the seed script to create default rules</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Rule
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Severity
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Threshold
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Fired
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rules.map((rule) => (
                <tr
                  key={rule.id}
                  className="hover:bg-slate-50/50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {rule.name}
                      </p>
                      {rule.description && (
                        <p className="text-xs text-slate-400 line-clamp-1 max-w-[250px]">
                          {rule.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                      {rule.type.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <SeverityBadge severity={rule.severity} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {rule.comparisonOp} {rule.threshold}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600">
                      {rule._count?.alertHistory ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        toggleMutation.mutate({
                          id: rule.id,
                          enabled: !rule.enabled,
                        })
                      }
                      className="flex items-center gap-1.5"
                    >
                      {rule.enabled ? (
                        <ToggleRight className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-slate-400" />
                      )}
                      <span
                        className={`text-xs font-medium ${
                          rule.enabled ? "text-emerald-600" : "text-slate-400"
                        }`}
                      >
                        {rule.enabled ? "Active" : "Disabled"}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        setEditing(rule);
                        setShowForm(true);
                      }}
                      className="mr-1 rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                      title="Edit rule"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete rule "${rule.name}"? This cannot be undone.`,
                          )
                        ) {
                          deleteMutation.mutate(rule.id);
                        }
                      }}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Delete rule"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AlertRuleForm({
  rule,
  onClose,
  onChanged,
}: {
  rule?: AlertRule | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    type: rule?.type ?? "low_health_score",
    name: rule?.name ?? "",
    description: rule?.description ?? "",
    severity: rule?.severity ?? "warning",
    threshold: String(rule?.threshold ?? 60),
    comparisonOp: rule?.comparisonOp ?? "lt",
    evaluationWindowMin: String(rule?.evaluationWindowMin ?? 60),
    consecutiveBreaches: String(rule?.consecutiveBreaches ?? 2),
    applyToAll: rule?.applyToAll ?? true,
  });
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        threshold: Number(form.threshold),
        evaluationWindowMin: Number(form.evaluationWindowMin),
        consecutiveBreaches: Number(form.consecutiveBreaches),
        actions: [{ type: "log" }],
      };
      return rule?.id
        ? alertService.updateRule(rule.id, payload as any)
        : alertService.createRule(payload as any);
    },
    onSuccess: () => {
      toast.success(rule?.id ? "Alert rule updated" : "Alert rule created");
      onChanged();
    },
    onError: () => toast.error("Alert rule was not saved"),
  });
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <RuleInput
          label="Name"
          value={form.name}
          onChange={(value) => setForm({ ...form, name: value })}
        />
        <RuleInput
          label="Metric type"
          value={form.type}
          onChange={(value) => setForm({ ...form, type: value })}
        />
        <RuleInput
          label="Severity"
          value={form.severity}
          onChange={(value) =>
            setForm({
              ...form,
              severity: value as AlertRule["severity"],
            })
          }
        />
        <RuleInput
          label="Comparison (lt, gt, lte, gte)"
          value={form.comparisonOp}
          onChange={(value) => setForm({ ...form, comparisonOp: value })}
        />
        <RuleInput
          label="Threshold"
          value={form.threshold}
          onChange={(value) => setForm({ ...form, threshold: value })}
        />
        <RuleInput
          label="Window minutes"
          value={form.evaluationWindowMin}
          onChange={(value) => setForm({ ...form, evaluationWindowMin: value })}
        />
        <RuleInput
          label="Consecutive breaches"
          value={form.consecutiveBreaches}
          onChange={(value) => setForm({ ...form, consecutiveBreaches: value })}
        />
        <RuleInput
          label="Description"
          value={form.description}
          onChange={(value) => setForm({ ...form, description: value })}
        />
      </div>
      <label className="mt-3 flex gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.applyToAll}
          onChange={(event) =>
            setForm({ ...form, applyToAll: event.target.checked })
          }
        />
        Apply to all tenants
      </label>
      <div className="mt-4 flex gap-2">
        <button
          disabled={!form.name || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          {rule?.id ? "Save changes" : "Create rule"}
        </button>
        <button
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-bold"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function RuleInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
      />
    </label>
  );
}

// ── Shared ─────────────────────────────────────────────────

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Bell;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}
